/**
 * ViewPortrait — read-only summary of the onboarding survey answers, split into
 * the journey questions and the readiness ratings (mobile PortraitView parity).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSurvey } from '@wlb/shared/context/SurveyContext';
import useHydrateSurvey from '@wlb/shared/hooks/useHydrateSurvey';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { Button } from '../components/ui/Button';
import { Tabs } from '../components/ui/Tabs';
import { ChevronLeftIcon } from '../components/ui/icons';
import {
  buildJourneyAnswers,
  buildReadinessRatings,
  isPortraitEmpty,
} from '../features/profile/portraitAnswers';
import { withEditFlag } from '../features/profile/portraitEdit';
import { ProfileCardSkeleton } from '../features/profile/ProfileSkeletons';
import '../features/profile/profile.css';

type PortraitTab = 'journey' | 'readiness';

const MAX_RATING = 5;

export function ViewPortrait() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { surveyData } = useSurvey();
  const [activeKey, setActiveKey] = useState<PortraitTab>('journey');
  const { loading } = useHydrateSurvey();

  const journey = useMemo(() => buildJourneyAnswers(surveyData, t), [surveyData, t]);
  const readiness = useMemo(() => buildReadinessRatings(surveyData, t), [surveyData, t]);
  const empty = isPortraitEmpty(surveyData);

  /** Journey answers start at q1; the readiness sliders are the last question. */
  const editPath = withEditFlag(activeKey === 'journey' || empty ? '/survey/q1' : '/survey/q9', true);

  const items = [
    { key: 'journey' as const, label: t('profile.portrait.journeyTab') },
    { key: 'readiness' as const, label: t('profile.portrait.readinessTab') },
  ];

  return (
    <section className="profile-page" aria-label={t('profile.header.viewPortrait')}>
      <div className="profile-topbar">
        <button
          type="button"
          className="profile-topbar__back"
          aria-label={t('profile.accessibility.back')}
          onClick={() => navigate('/profile')}
        >
          <ChevronLeftIcon size={20} />
        </button>
        <h1 className="profile-topbar__title">{t('profile.header.viewPortrait')}</h1>
      </div>

      <section className="portrait-card">
        <h2 className="portrait-card__heading">{t('profile.portrait.title')}</h2>
        <p className="portrait-card__subtitle">{t('profile.portrait.subtitle')}</p>
        {!empty && (
          <Button
            size="sm"
            variant="outline"
            className="portrait-card__edit"
            onClick={() => navigate(editPath)}
          >
            {t(activeKey === 'journey' ? 'profile.buttons.editJourney' : 'profile.buttons.editReadiness')}
          </Button>
        )}
      </section>

      {loading ? (
        <ProfileCardSkeleton />
      ) : empty ? (
        <div className="profile-restricted">
          <p className="profile-restricted__message">{t('profile.portrait.empty')}</p>
          <Button size="sm" onClick={() => navigate(editPath)}>
            {t('profile.portrait.startSurvey')}
          </Button>
        </div>
      ) : (
        <>
          <Tabs
            items={items}
            activeKey={activeKey}
            onChange={setActiveKey}
            label={t('profile.header.viewPortrait')}
            idPrefix="portrait-tab"
          />

          <div
            role="tabpanel"
            id={`portrait-tab-panel-${activeKey}`}
            aria-labelledby={`portrait-tab-${activeKey}`}
            className="portrait-list"
          >
            {activeKey === 'journey'
              ? journey.map((answer, index) => (
                  <div className="portrait-item" key={answer.key}>
                    <span className="portrait-item__question">{index + 1}. {answer.question}</span>
                    <span className="portrait-item__answer">{answer.answer}</span>
                  </div>
                ))
              : readiness.map((rating, index) => (
                  <div className="portrait-item" key={rating.key}>
                    <span className="portrait-item__question">{index + 1}. {rating.question}</span>
                    <div className="portrait-rating">
                      <span className="portrait-item__answer">{rating.leftLabel}</span>
                      <span className="portrait-rating__value">
                        {t('survey.accessibility.starRating', {
                          rating: rating.value,
                          total: MAX_RATING,
                        })}
                      </span>
                      <span className="portrait-item__answer">{rating.rightLabel}</span>
                    </div>
                  </div>
                ))}
          </div>
        </>
      )}
    </section>
  );
}
